import { useCallback, useEffect, useRef, useState } from "react";

import {
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from "./notificationApi";

const DEFAULT_POLL_INTERVAL_MS = 60000;

function isAbortError(error) {
  return error?.name === "AbortError";
}

function isPageActive() {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return true;
  }
  return !document.hidden && document.hasFocus();
}

export default function useNotificationState({
  userId,
  authToken,
  open,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [stateUserId, setStateUserId] = useState(userId || null);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [actionError, setActionError] = useState(null);

  const mountedRef = useRef(true);
  const userIdRef = useRef(userId || null);
  const authTokenRef = useRef(authToken || null);
  const openRef = useRef(Boolean(open));
  const requestRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const markOneRequestsRef = useRef(new Map());
  const markAllRequestRef = useRef(null);
  const mutationSequenceRef = useRef(0);
  const notificationsRef = useRef([]);

  notificationsRef.current = notifications;
  userIdRef.current = userId || null;
  authTokenRef.current = authToken || null;
  openRef.current = Boolean(open);

  const cancelReadRequest = useCallback(() => {
    requestSequenceRef.current += 1;
    requestRef.current?.controller.abort();
    requestRef.current = null;
  }, []);

  const runReadRequest = useCallback(
    (kind, { showLoading = false } = {}) => {
      const requestUserId = userIdRef.current;
      if (!requestUserId) {
        return Promise.resolve(false);
      }

      const existing = requestRef.current;
      if (existing?.kind === kind && existing.userId === requestUserId) {
        return existing.promise;
      }

      cancelReadRequest();
      const controller = new AbortController();
      const sequence = requestSequenceRef.current;
      if (showLoading) setLoading(true);

      const promise = (async () => {
        try {
          const payload =
            kind === "list"
              ? await fetchNotifications({
                  authToken: authTokenRef.current,
                  signal: controller.signal,
                })
              : await fetchUnreadCount({
                  authToken: authTokenRef.current,
                  signal: controller.signal,
                });

          if (
            !mountedRef.current ||
            controller.signal.aborted ||
            sequence !== requestSequenceRef.current ||
            requestUserId !== userIdRef.current
          ) {
            return false;
          }

          if (kind === "list") {
            setNotifications(payload.notifications);
          }
          setUnreadCount(payload.unreadCount);
          return true;
        } catch (error) {
          if (!isAbortError(error)) {
            console.error(`Failed to refresh notification ${kind}`, error);
          }
          return false;
        } finally {
          if (
            mountedRef.current &&
            sequence === requestSequenceRef.current &&
            requestUserId === userIdRef.current
          ) {
            if (showLoading) setLoading(false);
            requestRef.current = null;
          }
        }
      })();

      requestRef.current = { controller, kind, promise, userId: requestUserId };
      return promise;
    },
    [cancelReadRequest]
  );

  const refreshList = useCallback(
    (options) => runReadRequest("list", options),
    [runReadRequest]
  );

  const refreshUnreadCount = useCallback(
    () => runReadRequest("count"),
    [runReadRequest]
  );

  const refreshForCurrentView = useCallback(() => {
    return openRef.current ? refreshList() : refreshUnreadCount();
  }, [refreshList, refreshUnreadCount]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelReadRequest();
    };
  }, [cancelReadRequest]);

  useEffect(() => {
    cancelReadRequest();
    mutationSequenceRef.current += 1;
    markOneRequestsRef.current.clear();
    markAllRequestRef.current = null;
    setNotifications([]);
    setUnreadCount(0);
    setStateUserId(userId || null);
    setLoading(false);
    setMarkingAll(false);
    setActionError(null);

    if (userId) {
      refreshList({ showLoading: true });
    }
  }, [cancelReadRequest, refreshList, userId]);

  useEffect(() => {
    if (open && userId) {
      refreshList({ showLoading: true });
    }
  }, [open, refreshList, userId]);

  useEffect(() => {
    if (!userId || typeof window === "undefined") return undefined;

    let intervalId = null;
    let active = isPageActive();

    const stopPolling = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const startPolling = () => {
      stopPolling();
      intervalId = window.setInterval(refreshForCurrentView, pollIntervalMs);
    };
    const syncPolling = () => {
      const nextActive = isPageActive();
      if (nextActive && !active) {
        refreshForCurrentView();
        startPolling();
      } else if (!nextActive && active) {
        stopPolling();
      }
      active = nextActive;
    };

    if (active) startPolling();
    document.addEventListener("visibilitychange", syncPolling);
    window.addEventListener("focus", syncPolling);
    window.addEventListener("blur", syncPolling);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", syncPolling);
      window.removeEventListener("focus", syncPolling);
      window.removeEventListener("blur", syncPolling);
    };
  }, [pollIntervalMs, refreshForCurrentView, userId]);

  const markOneRead = useCallback(
    (notificationId) => {
      const normalizedId = String(notificationId || "").trim();
      const requestUserId = userIdRef.current;
      if (!normalizedId || !requestUserId) return Promise.resolve(false);

      const currentNotification = notificationsRef.current.find(
        (notification) => notification?.id === normalizedId
      );
      if (currentNotification?.read) return Promise.resolve(true);

      const existing = markOneRequestsRef.current.get(normalizedId);
      if (existing) return existing;

      cancelReadRequest();
      const mutationSequence = mutationSequenceRef.current + 1;
      mutationSequenceRef.current = mutationSequence;
      setActionError(null);
      const promise = (async () => {
        try {
          const payload = await markNotificationRead(normalizedId, {
            authToken: authTokenRef.current,
          });
          if (!mountedRef.current || requestUserId !== userIdRef.current) {
            return false;
          }
          if (mutationSequence === mutationSequenceRef.current) {
            cancelReadRequest();
          }
          setNotifications((current) =>
            current.map((notification) =>
              notification?.id === normalizedId
                ? { ...notification, read: true }
                : notification
            )
          );
          if (mutationSequence === mutationSequenceRef.current) {
            setUnreadCount(payload.unreadCount);
          }
          return true;
        } catch (error) {
          console.error("Failed to mark notification read", error);
          if (
            mountedRef.current &&
            requestUserId === userIdRef.current &&
            mutationSequence === mutationSequenceRef.current
          ) {
            setActionError("markOne");
            await refreshList();
          }
          return false;
        } finally {
          if (markOneRequestsRef.current.get(normalizedId) === promise) {
            markOneRequestsRef.current.delete(normalizedId);
          }
        }
      })();

      markOneRequestsRef.current.set(normalizedId, promise);
      return promise;
    },
    [cancelReadRequest, refreshList]
  );

  const markAllRead = useCallback(() => {
    const requestUserId = userIdRef.current;
    if (!requestUserId || unreadCount <= 0) return Promise.resolve(false);
    if (markAllRequestRef.current) return markAllRequestRef.current;

    cancelReadRequest();
    const mutationSequence = mutationSequenceRef.current + 1;
    mutationSequenceRef.current = mutationSequence;
    setActionError(null);
    setMarkingAll(true);
    const promise = (async () => {
      try {
        const payload = await markAllNotificationsRead({
          authToken: authTokenRef.current,
        });
        if (!mountedRef.current || requestUserId !== userIdRef.current) {
          return false;
        }
        if (mutationSequence === mutationSequenceRef.current) {
          cancelReadRequest();
        }
        setNotifications((current) =>
          current.map((notification) => ({ ...notification, read: true }))
        );
        if (mutationSequence === mutationSequenceRef.current) {
          setUnreadCount(payload.unreadCount);
        }
        return true;
      } catch (error) {
        console.error("Failed to mark all notifications read", error);
        if (
          mountedRef.current &&
          requestUserId === userIdRef.current &&
          mutationSequence === mutationSequenceRef.current
        ) {
          setActionError("markAll");
        }
        return false;
      } finally {
        if (mountedRef.current && requestUserId === userIdRef.current) {
          setMarkingAll(false);
        }
        if (markAllRequestRef.current === promise) {
          markAllRequestRef.current = null;
        }
      }
    })();

    markAllRequestRef.current = promise;
    return promise;
  }, [cancelReadRequest, unreadCount]);

  const stateMatchesUser = stateUserId === (userId || null);

  return {
    actionError: stateMatchesUser ? actionError : null,
    loading: stateMatchesUser ? loading : false,
    markAllRead,
    markOneRead,
    markingAll: stateMatchesUser ? markingAll : false,
    notifications: stateMatchesUser ? notifications : [],
    refreshList,
    unreadCount: stateMatchesUser ? unreadCount : 0,
  };
}
